import { Fragment } from 'react';
import Link from 'next/link';
import classNames from 'classnames';
import i18n from '@/settings/i18n';

const t = i18n.init('cart');

export type PurchaseFlowStep = 'accessories' | 'cart' | 'checkout';

export const PURCHASE_FLOW_STEPS: PurchaseFlowStep[] = [
  'accessories',
  'cart',
  'checkout',
];

const stepLabel = (step: PurchaseFlowStep) => {
  switch (step) {
    case 'accessories':
      return t`Accessories`;
    case 'cart':
      return t`Cart`;
    default:
      return t`Checkout`;
  }
};

type PurchaseFlowStepperProps = {
  current: PurchaseFlowStep;
  /**
   * Steps to show, in order and numbered by position; Accessories is left out
   * when that step was skipped. Defaults to all three.
   */
  steps?: PurchaseFlowStep[];
  links?: Partial<Record<PurchaseFlowStep, string>>;
  fill?: boolean;
  className?: string;
};

/** Accessories - Cart - Checkout progress bar of the purchase flow. */
const PurchaseFlowStepper = ({
  current,
  steps = PURCHASE_FLOW_STEPS,
  links = {},
  fill = false,
  className,
}: PurchaseFlowStepperProps) => {
  const currentIndex = steps.indexOf(current);

  return (
    <nav aria-label={t`Purchase progress`} className={className}>
      <ol
        className={classNames(
          'flex items-center',
          fill ? 'w-full justify-between gap-2 text-[13px]' : 'gap-4 text-sm',
        )}
      >
        {steps.map((step, index) => {
          const isReached = index <= currentIndex;
          const isCurrent = index === currentIndex;
          const href = index < currentIndex ? links[step] : undefined;

          const content = (
            <>
              <span
                className={classNames(
                  'flex shrink-0 items-center justify-center rounded-full',
                  fill ? 'h-5 w-5 text-[11px]' : 'h-6 w-6 text-xs',
                  isReached
                    ? 'bg-gray-700 text-white'
                    : 'border border-[#ECEDED] text-[#BBBCBC]',
                )}
              >
                {index + 1}
              </span>
              <span
                className={classNames(
                  'whitespace-nowrap',
                  isReached ? 'text-gray-700' : 'text-[#BBBCBC]',
                  isCurrent && 'font-medium',
                )}
              >
                {stepLabel(step)}
              </span>
            </>
          );

          return (
            <Fragment key={step}>
              {index > 0 && (
                <li
                  aria-hidden
                  className={classNames(
                    'h-px bg-[#ECEDED]',
                    // Whatever room the steps leave: none on the narrowest
                    // phones, where the lines vanish and the steps spread out.
                    fill ? 'min-w-0 flex-1' : 'w-12 shrink-0',
                  )}
                />
              )}
              <li aria-current={isCurrent ? 'step' : undefined}>
                {href ? (
                  <Link
                    href={href}
                    className={classNames(
                      'flex items-center hover:underline',
                      fill ? 'gap-1.5' : 'gap-2',
                    )}
                  >
                    {content}
                  </Link>
                ) : (
                  <span
                    className={classNames(
                      'flex items-center',
                      fill ? 'gap-1.5' : 'gap-2',
                    )}
                  >
                    {content}
                  </span>
                )}
              </li>
            </Fragment>
          );
        })}
      </ol>
    </nav>
  );
};

export default PurchaseFlowStepper;
